import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";

import { setServio } from "../../../../../../redux/servioSlice";
import { RootState } from "../../../../../../redux/store";
import { DEFAULT_MAIN_COLOR } from "@constants/index";

import { Container, SectionTitle } from "./styled";
import { PageTitleCard } from "./PageTitleCard";
import { FiltersCard } from "./FiltersCard";
import { SortCard } from "./SortCard";
import {
    TablesState,
    FilterKey,
    FilterConfig,
    SortKey,
    DEFAULT_FILTERS,
    DEFAULT_SORT_OPTIONS,
} from "./types";

export function Tables(): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor =
        useSelector((s: RootState) => s.global.infoUser.maincolor) ||
        DEFAULT_MAIN_COLOR;

    const reduxHome = useSelector((s: RootState) => s.servio.home) as Partial<
        TablesState
    >;

    const state: TablesState = React.useMemo(
        () => ({
            titleEnabled: reduxHome.titleEnabled ?? true,
            filters: { ...DEFAULT_FILTERS, ...(reduxHome.filters || {}) },
            sortOptions: {
                ...DEFAULT_SORT_OPTIONS,
                ...(reduxHome.sortOptions || {}),
            },
        }),
        [reduxHome]
    );

    const [activeColorPicker, setActiveColorPicker] = React.useState<
        string | null
    >(null);

    const updateSection = (updates: Partial<TablesState>): void => {
        dispatch(setServio({ home: { ...state, ...updates } }));
    };

    const resetTitle = (): void => updateSection({ titleEnabled: true });
    const resetFilters = (): void =>
        updateSection({ filters: { ...DEFAULT_FILTERS } });
    const resetSort = (): void =>
        updateSection({ sortOptions: { ...DEFAULT_SORT_OPTIONS } });

    const updateFilter = (
        key: FilterKey,
        updates: Partial<FilterConfig>
    ): void => {
        updateSection({
            filters: {
                ...state.filters,
                [key]: { ...state.filters[key], ...updates },
            },
        });
    };

    const toggleSortOption = (key: SortKey): void => {
        updateSection({
            sortOptions: {
                ...state.sortOptions,
                [key]: !state.sortOptions[key],
            },
        });
    };

    return (
        <Container data-testid="servio-home-tab">
            <div>
                <SectionTitle>{t("Home page")}</SectionTitle>
            </div>

            <PageTitleCard
                titleEnabled={state.titleEnabled}
                mainColor={mainColor}
                onToggle={() =>
                    updateSection({ titleEnabled: !state.titleEnabled })
                }
                onReset={resetTitle}
            />

            <FiltersCard
                filters={state.filters}
                mainColor={mainColor}
                activeColorPicker={activeColorPicker}
                setActiveColorPicker={setActiveColorPicker}
                onUpdateFilter={updateFilter}
                onReset={resetFilters}
            />

            <SortCard
                sortOptions={state.sortOptions}
                mainColor={mainColor}
                onToggle={toggleSortOption}
                onReset={resetSort}
            />
        </Container>
    );
}
